var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { createRef, Fragment, isValidElement, PureComponent } from 'react';
import { Alert, ModalType, SensitiveData, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, object, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { CollapseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, generateUUID, isFunction } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as AlertIcon } from '../../../resources/assets/components/DLS_Alert.svg';
import { ReactComponent as Birthday } from '../../../resources/assets/components/DLS_Birthday.svg';
import { ReactComponent as FallRisk } from '../../../resources/assets/components/DLS_Fall_risk.svg';
import { ReactComponent as HideIcon } from '../../../resources/assets/components/DLS_Hide.svg';
import { ReactComponent as Isolation } from '../../../resources/assets/components/DLS_Isolation.svg';
import { ReactComponent as Preventive } from '../../../resources/assets/components/DLS_Preventive.svg';
import { ReactComponent as ShowIcon } from '../../../resources/assets/components/DLS_Show.svg';
import { ReactComponent as VIP } from '../../../resources/assets/components/DLS_VIP.svg';
import { hideSensitiveData } from '../../utils/hide-sensitive-data.js';
import { Expander } from '../expander/expander.js';
import { FieldOrientation } from '../field/field-constants.js';
import { Field } from '../field/field.js';
import { InlineImage } from '../inline-image/inline-image.js';
import { Modal } from '../modal/modal.js';
import { TextFontSize } from '../text/text-constants.js';
import { Text } from '../text/text.js';
import { InformationBarExpander } from './components/information-bar-expander.js';
import { InformationBarSecondaryRow } from './components/information-bar-secondary-row.js';
import InformationBarAPI from './information-bar-api.js';
import { InformationBarAlertType } from './information-bar-constants.js';
import './information-bar.css';
let InformationBar = class InformationBar extends PureComponent {
    constructor() {
        super(...arguments);
        this.mainContainerRef = createRef();
        this.primaryRowContainerRef = createRef();
        this.state = {
            alertsCollapsed: true,
            collapsed: true,
            shouldMaskFieldOneSensitiveData: this.props.fieldOneSensitiveData !== SensitiveData.NONE,
            shouldMaskFieldTwoSensitiveData: this.props.fieldTwoSensitiveData !== SensitiveData.NONE,
        };
        this.ICONS_MAP = new Map([
            [Alert.ALLERGIES, () => React.createElement(AlertIcon, { className: "c-information-bar__icon-alert", key: Alert.ALLERGIES })],
            [Alert.BIRTHDAY, () => React.createElement(Birthday, { className: "c-information-bar__icon-birthday", key: Alert.BIRTHDAY })],
            [Alert.FALL_RISK, () => React.createElement(FallRisk, { className: "c-information-bar__icon-alert", key: Alert.FALL_RISK })],
            [Alert.ISOLATION, () => React.createElement(Isolation, { key: Alert.ISOLATION })],
            [Alert.PREVENTIVE_MEDICINE, () => React.createElement(Preventive, { className: "c-information-bar__icon-preventive", key: Alert.PREVENTIVE_MEDICINE })],
            [Alert.VIP, () => React.createElement(VIP, { className: "c-information-bar__icon-vip", key: Alert.VIP })],
        ]);
        this.onApplicationBarScroll = (event) => {
            const { accomodateApplicationBar } = event.detail;
            this.setState({ accomodateApplicationBar });
        };
        this.toggleFullView = () => {
            const { fullViewLocked, onCollapse } = this.props;
            if (fullViewLocked) {
                return;
            }
            const triggerOnCollapse = () => {
                const collapsed = this.state.collapsed;
                isFunction(onCollapse) && onCollapse(new CollapseEvent({ collapsed }));
            };
            this.setState((prevState) => ({
                collapsed: !prevState.collapsed,
            }), triggerOnCollapse);
        };
        this.toggleAlerts = (event) => {
            this.setState((prevState) => ({ alertsCollapsed: !prevState.alertsCollapsed }));
            event.stopPropagation();
        };
        this.applyCollapseAreaHeight = () => {
            const div = this.primaryRowContainerRef.current;
            if (!div) {
                return;
            }
            const { y, height } = div.getBoundingClientRect();
            const difference = window.outerHeight - y - height;
            const actualHeight = height + difference;
            div.style.minHeight = `${actualHeight}px`;
        };
    }
    componentDidMount() {
        const { fullViewLocked } = this.props;
        const element = this.mainContainerRef.current;
        if (!element) {
            return;
        }
        // Have to cast it as an EventListener, otherwise could not use `event.detail` within it.
        element.addEventListener('oswald-application-bar-scroll', this.onApplicationBarScroll);
        if (fullViewLocked) {
            this.applyCollapseAreaHeight();
        }
    }
    componentDidUpdate(prevProps) {
        const { fullViewLocked } = this.props;
        if (fullViewLocked && prevProps.fullViewLocked !== fullViewLocked) {
            this.applyCollapseAreaHeight();
        }
    }
    componentWillUnmount() {
        const element = this.mainContainerRef.current;
        if (!element) {
            return;
        }
        // Have to cast it as an EventListener, otherwise could not use `event.detail` within it.
        element.removeEventListener('oswald-application-bar-scroll', this.onApplicationBarScroll);
    }
    getIcon(alert) {
        const iconFunction = this.ICONS_MAP.get(alert);
        if (isFunction(iconFunction)) {
            return iconFunction();
        }
        return null;
    }
    getFirstAlertIcon() {
        const { alerts, highlightedList } = this.props;
        if (highlightedList) {
            return this.getIcon(Alert.ALLERGIES);
        }
        if (!alerts) {
            return null;
        }
        const allKeys = Object.keys(alerts);
        const keys = allKeys.filter(key => key !== Alert.ISOLATION);
        const alert = keys.length > 0 ? keys[0] : null;
        return alert ? this.getIcon(alert) : null;
    }
    renderFirstAlertIcon() {
        const alertIcon = this.getFirstAlertIcon();
        if (!alertIcon) {
            return null;
        }
        return (React.createElement("div", { className: "c-information-bar__alert-icon" }, alertIcon));
    }
    getIsolationIcon() {
        const { alerts } = this.props;
        if (!alerts || !alerts.isolation) {
            return null;
        }
        return this.getIcon(Alert.ISOLATION);
    }
    getVisibleExtraInformation() {
        const { extraInformation } = this.props;
        if (!extraInformation) {
            return [];
        }
        return Object.values(extraInformation)
            .filter(info => info.visible);
    }
    renderToggleHiddenValue(sensitiveData, stateProp) {
        if (sensitiveData === SensitiveData.NONE) {
            return null;
        }
        const toggleHiddenValue = (event) => {
            event.stopPropagation();
            this.setState(prevState => ({ ...prevState, [stateProp]: !prevState[stateProp] }));
        };
        return (React.createElement("div", { className: "c-information-bar__text--toggle-privacy", onClick: toggleHiddenValue }, this.state[stateProp] ? React.createElement(ShowIcon, null) : React.createElement(HideIcon, null)));
    }
    renderHeaderInfo() {
        const { fieldOneSensitiveData, fieldOneTitle = '', fieldOneValue = '', fieldTwoSensitiveData, fieldTwoTitle = '', fieldTwoValue = '', fullViewLocked, highlightFieldOne, highlightFieldTwo, title, } = this.props;
        const { collapsed } = this.state;
        const isCollapsed = collapsed && !fullViewLocked;
        const headerClassName = classNames({
            'c-information-bar__header-fields': true,
            'is-collapsed': isCollapsed,
        });
        const classNameFieldOne = classNames({
            'c-information-bar__header--highlighted': Boolean(highlightFieldOne),
            'c-information-bar__header-field': true,
        });
        const classNameFieldTwo = classNames({
            'c-information-bar__header--highlighted': Boolean(highlightFieldTwo),
            'c-information-bar__header-field': true,
            'c-information-bar__header-field-right': true,
        });
        const isolationIcon = this.getIsolationIcon();
        return (React.createElement("div", { className: "c-information-bar__header-info" },
            React.createElement("div", { className: "c-information-bar__header-info-content" },
                React.createElement("div", { className: "c-information-bar__header-title" },
                    React.createElement(Text, { className: "c-information-bar__text--data", ellipsis: true, value: title })),
                React.createElement("div", { className: headerClassName },
                    React.createElement("div", { className: classNameFieldOne },
                        React.createElement("label", { className: "c-information-bar__text c-information-bar__text--label" }, `${MessageService.translate(fieldOneTitle)} `),
                        React.createElement("span", { className: "c-information-bar__text c-information-bar__text--data u-truncate" }, hideSensitiveData(MessageService.translate(fieldOneValue), this.state.shouldMaskFieldOneSensitiveData ? fieldOneSensitiveData : SensitiveData.NONE)),
                        this.renderToggleHiddenValue(fieldOneSensitiveData, 'shouldMaskFieldOneSensitiveData')),
                    !fullViewLocked && !isolationIcon
                        ? React.createElement("div", { className: classNameFieldTwo },
                            React.createElement("label", { className: "c-information-bar__text c-information-bar__text--label" }, `${MessageService.translate(fieldTwoTitle)} `),
                            React.createElement("span", { className: "c-information-bar__text c-information-bar__text--data u-truncate" }, hideSensitiveData(MessageService.translate(fieldTwoValue), this.state.shouldMaskFieldTwoSensitiveData ? fieldTwoSensitiveData : SensitiveData.NONE)),
                            this.renderToggleHiddenValue(fieldOneSensitiveData, 'shouldMaskFieldTwoSensitiveData'))
                        : null)),
            React.createElement("div", { className: "c-information-bar__header-info-icons" },
                this.renderFirstAlertIcon(),
                this.renderAlertsType(InformationBarAlertType.ICON))));
    }
    renderField(title) {
        return (React.createElement("label", { className: "u-truncate c-information-bar__text c-information-bar__text--label c-information-bar__extra-info-inline", key: title }, `${MessageService.translate(title)} `));
    }
    renderImage() {
        const { image } = this.props;
        if (!image) {
            return null;
        }
        return React.createElement(InlineImage, { className: "c-information-bar__avatar-img", src: image });
    }
    renderData() {
        return (React.createElement("div", { className: "c-information-bar__avatar-data" }, this.renderHeaderInfo()));
    }
    renderHeader() {
        return (React.createElement("div", { className: "c-information-bar__avatar" },
            this.renderImage(),
            this.renderData()));
    }
    renderBody() {
        return (React.createElement(Fragment, null,
            this.renderAlerts(),
            this.renderExtraInformation()));
    }
    renderAlertsList() {
        const { alerts, highlightedList } = this.props;
        const { alertsCollapsed: renderOnlyFirstTwo } = this.state;
        let countAlerts = 0;
        const alertsArray = [];
        if (highlightedList) {
            countAlerts++;
            alertsArray.push(this.renderHighlightedList(highlightedList));
        }
        if (!alerts) {
            return alertsArray;
        }
        Object.entries(alerts).every(([alertKey, alert]) => {
            if (countAlerts > 1 && renderOnlyFirstTwo) {
                return false;
            }
            countAlerts++;
            alertsArray.push(this.renderAlertRow(alertKey, alert));
            return true;
        });
        return (alertsArray);
    }
    renderAlerts() {
        const { alerts = {}, highlightedList } = this.props;
        const { alertsCollapsed } = this.state;
        const countAlerts = Object.keys(alerts).length + (highlightedList ? 1 : 0);
        if (countAlerts === 0) {
            return null;
        }
        const shouldRenderExpander = countAlerts > 2 || highlightedList;
        return (React.createElement(InformationBarSecondaryRow, { className: "c-information-bar__row-alerts-area", onClick: this.toggleAlerts },
            React.createElement("div", { className: "c-information-bar__list-title" }, shouldRenderExpander ? React.createElement(InformationBarExpander, { expanded: alertsCollapsed }) : null),
            React.createElement("div", { className: "c-information-bar__list" }, this.renderAlertsList())));
    }
    renderHighlightedList(highlightedList) {
        const { alertsCollapsed } = this.state;
        const informationCount = highlightedList.information.length;
        return (React.createElement("div", { className: "c-information-bar__list-item", key: Alert.ALLERGIES },
            React.createElement("div", { className: "c-information-bar__list" },
                React.createElement("div", { className: "c-information-bar__list-icon" }, this.getIcon(Alert.ALLERGIES)),
                React.createElement("span", { className: "c-information-bar__list-item-text c-information-bar__text c-information-bar__text--data u-text-bold" }, `${MessageService.translate(highlightedList.title || 'tws.information-bar.highlighted-list')} (${informationCount})`),
                React.createElement("div", { className: "c-information-bar__list c-information-bar__list-item c-information-bar__highlighted-list" }, alertsCollapsed ? null : highlightedList.information.map((highlightedInformation) => this.renderHighlightedInformation(highlightedInformation))))));
    }
    renderHighlightedInformation(highlighted) {
        return (React.createElement("div", { className: "c-information-bar__list-item", key: generateUUID() },
            React.createElement(Field, { className: "c-information-bar__text c-information-bar__text--label", discloseableSensitiveData: true, entry: "fake-entry", label: highlighted.label, sensitiveData: highlighted.sensitiveData, uuid: "fake-uuid", width: 12 },
                React.createElement(Text, { className: "c-information-bar__text c-information-bar__text--data" }, highlighted.description))));
    }
    renderIcon(icon, className) {
        if (isValidElement(icon)) {
            return icon;
        }
        return React.createElement(InlineImage, { className: className, src: icon });
    }
    renderAlertRow(alertKey, alert) {
        const { alertsCollapsed } = this.state;
        const hasItems = Boolean(alert.items) && Boolean(alert.items.length);
        const key = `${alertKey}-${alertsCollapsed}`;
        const className = classNames({
            'c-information-bar__list-item': !hasItems,
            'c-information-bar__list-item--highlighted': Boolean(alert.highlight) && !hasItems,
            'c-information-bar__list-item-expander-content': hasItems,
        });
        const AlertRow = () => {
            return (React.createElement("div", { className: className },
                React.createElement("div", { className: "c-information-bar__list-icon" }, alert.icon ? this.renderIcon(alert.icon, alert.iconClassName) : this.getIcon(alertKey)),
                React.createElement(Field, { className: "c-information-bar__list-item-text", data: { description: alert.description }, discloseableSensitiveData: true, entry: "description", orientation: FieldOrientation.HORIZONTAL, sensitiveData: alert.sensitiveData, uuid: "fake-uuid", width: 12 },
                    React.createElement(Text, { className: "c-information-bar__text c-information-bar__text--data" }))));
        };
        if (!hasItems) {
            return (React.createElement(AlertRow, { key: key }));
        }
        const classNameExpander = classNames({
            'c-information-bar__list-item--highlighted': Boolean(alert.highlight),
            'c-information-bar__list-item-expander': true,
        });
        return (React.createElement(Expander, { className: classNameExpander, key: key },
            React.createElement(AlertRow, { "data-slot": "expander-content" }),
            React.createElement("div", { className: "c-information-bar__list-item-expander-expandable-content", "data-slot": "expander-expandable-content" }, alert.items?.map((item, index) => (React.createElement(Text, { className: "c-information-bar__text c-information-bar__text--data", key: `alert-item-${index}`, value: item }))))));
    }
    renderExtraInformation() {
        const { extraInformationTitle = 'tws.information-bar.personal-information' } = this.props;
        const visibleInformations = this.getVisibleExtraInformation();
        if (visibleInformations.length === 0) {
            return null;
        }
        return (React.createElement(InformationBarSecondaryRow, null,
            React.createElement("div", { className: "c-information-bar__list-title" },
                React.createElement("span", { className: "c-information-bar__text c-information-bar__text--title u-truncate u-col-12" }, MessageService.translate(extraInformationTitle))),
            React.createElement("div", { className: "c-information-bar__list" }, visibleInformations.map((info) => this.renderExtraInformationRow(info)))));
    }
    renderExtraInformationRow(info) {
        const className = classNames({
            'c-information-bar__list-item': true,
            'c-information-bar__list-item--highlighted': Boolean(info.highlight),
        });
        return (React.createElement("div", { className: className, key: info.label },
            React.createElement(Field, { className: "c-information-bar__text c-information-bar__text--label", discloseableSensitiveData: true, entry: "fake-entry", label: info.label, sensitiveData: info.sensitiveData, uuid: "fake-uuid", width: 12 },
                React.createElement(Text, { className: "c-information-bar__text c-information-bar__text--data" }, info.value || 'tws.information-bar.na'))));
    }
    renderCompleteModal() {
        return (React.createElement(Modal, { className: "c-information-bar-modal t-color-grey", closeable: true, theme: ThemeTonalRange.VERY_DARK, type: ModalType.FULLSCREEN }, this.renderComplete()));
    }
    renderComplete() {
        return (React.createElement(Fragment, null,
            this.renderHeader(),
            this.renderBody()));
    }
    renderRowPrimaryContent() {
        const { fullViewLocked } = this.props;
        const { collapsed } = this.state;
        const visibleInformations = this.getVisibleExtraInformation();
        if (collapsed && !fullViewLocked) {
            return this.renderHeaderInfo();
        }
        if (fullViewLocked || !fullViewLocked && visibleInformations.length === 0) {
            return this.renderComplete();
        }
        return this.renderCompleteModal();
    }
    renderAlertsType(type) {
        const { collapsed } = this.state;
        const { alerts, fullViewLocked } = this.props;
        const isCollapsed = collapsed && !fullViewLocked;
        if (!alerts || !isCollapsed) {
            return null;
        }
        const alertsType = Object.entries(alerts).filter(([, alert]) => alert.type === type);
        if (!alertsType.length) {
            return null;
        }
        const count = alertsType.length > 4 ? alertsType.length - 3 : 0;
        const className = classNames({
            'c-information-bar__alert-badge': type === InformationBarAlertType.BADGE,
            'c-information-bar__alert-icon': type === InformationBarAlertType.ICON,
        });
        const flags = alertsType
            .slice(0, count ? 3 : 4)
            .map(([key, alert]) => (alert.icon ? this.renderIcon(alert.icon, alert.iconClassName) : this.getIcon(key)));
        const counter = count
            ? (React.createElement("div", { className: `${className}-count` },
                React.createElement(Text, { size: TextFontSize.XSMALL, value: `+${count}` }))) : null;
        return (React.createElement("div", { className: className },
            flags,
            counter));
    }
    render() {
        const { className, stickToTop } = this.props;
        const { accomodateApplicationBar } = this.state;
        const informationBarClassName = classNames({
            'c-information-bar': true,
            'c-information-bar--accomodate-application-bar': Boolean(accomodateApplicationBar),
            'c-information-bar--stick-to-top': Boolean(stickToTop),
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: informationBarClassName, onClick: this.toggleFullView, ref: this.mainContainerRef },
            React.createElement("div", { className: "c-information-bar__row c-information-bar__row--primary", ref: this.primaryRowContainerRef },
                this.renderAlertsType(InformationBarAlertType.BADGE),
                this.renderRowPrimaryContent())));
    }
};
InformationBar.propTypes = {
    alerts: object.notEnumerable(),
    alertsTitle: string,
    className: string,
    extraInformation: object.notEnumerable(),
    extraInformationTitle: string,
    fieldOneSensitiveData: oneOf(enumValues(SensitiveData)),
    fieldOneTitle: string,
    fieldOneValue: string,
    fieldTwoSensitiveData: oneOf(enumValues(SensitiveData)),
    fieldTwoTitle: string,
    fieldTwoValue: string,
    fullViewLocked: bool,
    highlightedList: object,
    highlightFieldOne: bool,
    highlightFieldTwo: bool,
    image: string,
    onCollapse: eventListener,
    stickToTop: bool,
    title: expression,
};
InformationBar.defaultProps = {
    alerts: {},
    alertsTitle: 'tws.information-bar.alerts',
    extraInformationTitle: 'tws.information-bar.personal-information',
    fieldOneSensitiveData: SensitiveData.NONE,
    fieldOneTitle: '',
    fieldTwoSensitiveData: SensitiveData.NONE,
    fieldTwoTitle: '',
    fullViewLocked: false,
    stickToTop: false,
    title: '',
};
InformationBar = __decorate([
    AwesomeComponent({ api: InformationBarAPI, type: ComponentType.DATA_DISPLAY })
], InformationBar);
export default InformationBar;
//# sourceMappingURL=information-bar.js.map